// path: frontend/src/components/pages/admin/models/index.jsx
import React, { useEffect, useState, useMemo } from "react";
import ModelsTable from "./ModelsTable";
import ModelsModal from "./ModelsModal";
import AddModelModal from "./AddModelModal";
import {
  getAllModels,
  updateModel,
  createModel,
  deleteModel,
} from "../../../../api/apiFunction/modelServices";
import { toast } from "react-toastify";

const ModelsPage = () => {
  const [models, setModels] = useState([]);
  const [editingModel, setEditingModel] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [loading, setLoading] = useState(true); // Start with true to show loader on mount

  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pagination, setPagination] = useState({});
  const PAGE_SIZE = 10;

  // Frontend filters (applied after backend search)
  const [typeFilter, setTypeFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [supportFilter, setSupportFilter] = useState("");

  const typeOptions = useMemo(
    () => Array.from(new Set(models.map((m) => m.type).filter(Boolean))),
    [models],
  );
  const providerOptions = useMemo(
    () => Array.from(new Set(models.map((m) => m.provider).filter(Boolean))),
    [models],
  );

  // Frontend filtering (only type, provider, support - search is backend)
  const filteredModels = useMemo(() => {
    return models.filter((model) => {
      const matchesType = !typeFilter || model.type === typeFilter;
      const matchesProvider =
        !providerFilter || model.provider === providerFilter;
      const matchesSupport =
        !supportFilter || (model.support && model.support[supportFilter]);
      return matchesType && matchesProvider && matchesSupport;
    });
  }, [models, typeFilter, providerFilter, supportFilter]);

  const fetchModels = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: PAGE_SIZE,
      };

      if (search && search.trim()) {
        params.search = search.trim();
      }

      const res = await getAllModels(params);

      // Handle response structure: data.models for paginated, data for non-paginated
      if (res.data?.models) {
        setModels(res.data.models || []);
        setPagination(res.data.pagination || {});
        setTotalPages(res.data.pagination?.totalPages || 1);
      } else {
        // Fallback for non-paginated response
        setModels(res.data || []);
        setTotalPages(1);
      }
    } catch {
      setModels([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels(currentPage, activeSearch);
  }, [currentPage, activeSearch]);

  // Handle filter button click
  const handleFilter = () => {
    setActiveSearch(searchTerm);
    setCurrentPage(1); // Reset to page 1 when filtering
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm("");
    setActiveSearch("");
    setCurrentPage(1);
  };

  const handleEdit = (model) => {
    setEditingModel(model);
    setModalOpen(true);
  };

  const handleSave = async (updated) => {
    setLoading(true);
    try {
      await updateModel(updated._id, updated);
      setModalOpen(false);
      setEditingModel(null);
      fetchModels(currentPage, activeSearch);
      toast.success("Model updated successfully");
    } catch (error) {
      toast.error("Failed to update model");
    } finally {
      setLoading(false);
    }
  };

  const handleAddModel = async (newModel) => {
    setLoading(true);
    try {
      await createModel(newModel);
      setAddModalOpen(false);
      fetchModels(currentPage, activeSearch);
      toast.success("Model added successfully");
    } catch (error) {
      toast.error("Failed to add model");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (modelId, isActive) => {
    try {
      // Optimistically update UI
      setModels((prev) =>
        prev.map((model) =>
          model._id === modelId ? { ...model, active: isActive } : model,
        ),
      );

      // Update in backend
      await updateModel(modelId, { active: isActive });
      toast.success(
        `Model ${isActive ? "activated" : "deactivated"} successfully`,
      );
    } catch (error) {
      // Revert UI if failed
      setModels((prev) =>
        prev.map((model) =>
          model._id === modelId ? { ...model, active: !isActive } : model,
        ),
      );
      toast.error("Failed to update model status");
    }
  };

  const handleDelete = async (model) => {
    try {
      setLoading(true);
      await deleteModel(model._id);
      toast.success("Model deleted successfully");
      await fetchModels(currentPage, activeSearch);
    } catch (error) {
      toast.error("Failed to delete model");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="models-page p-6 h-full overflow-y-auto custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        <ModelsTable
          models={filteredModels}
          onEdit={handleEdit}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          activeSearch={activeSearch}
          onFilter={handleFilter}
          onClearSearch={handleClearSearch}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          providerFilter={providerFilter}
          setProviderFilter={setProviderFilter}
          typeOptions={typeOptions}
          providerOptions={providerOptions}
          loading={loading}
          supportFilter={supportFilter}
          setSupportFilter={setSupportFilter}
          onDelete={handleDelete}
          onToggleActive={handleToggleActive}
          onAddModel={() => setAddModalOpen(true)}
          currentPage={currentPage}
          totalPages={totalPages}
          pagination={pagination}
          onPageChange={setCurrentPage}
        />
      </div>
      <ModelsModal
        isOpen={modalOpen}
        model={editingModel}
        onSave={handleSave}
        onClose={() => {
          setModalOpen(false);
          setEditingModel(null);
        }}
      />
      <AddModelModal
        isOpen={addModalOpen}
        onSave={handleAddModel}
        onClose={() => setAddModalOpen(false)}
      />
    </div>
  );
};

export default ModelsPage;
